<template>
  <section
    class="individual-signup"
    :style="{
      'background-image':
        'url(' + bucketUrl + '/assets/background_landing-page.jpg)'
    }"
  >
    <header-logged-out type="individual" :checkuser="false" />
    <v-container fill-height>
      <v-row class="fill-height" align="center" justify="center">
        <v-col cols="12" sm="10" md="8" lg="7" xl="5">
          <v-expand-transition>
            <v-card style="transition: 2s linear 1s;">
              <form @submit.prevent="submit">
                <v-toolbar dark color="primary" flat>
                  <v-toolbar-title>Registration</v-toolbar-title>
                  <v-spacer />
                </v-toolbar>
                <v-card-text>
                  <v-row row wrap>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="first_name"
                        v-validate="'required'"
                        :error-messages="errors.collect('first_name')"
                        label="First Name"
                        hint="Enter your first name"
                        data-vv-name="first_name"
                        data-vv-as="first name"
                        required
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="last_name"
                        v-validate="'required'"
                        :error-messages="errors.collect('last_name')"
                        label="Last Name"
                        hint="Enter your last name"
                        data-vv-name="last_name"
                        data-vv-as="last name"
                        required
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="email"
                        v-validate="'required|max:150'"
                        :error-messages="errors.collect('email')"
                        label="E-mail"
                        data-vv-name="email"
                        data-vv-as="email"
                        required
                      />
                    </v-col>
                    <v-col cols="12">
                      <password-strength
                        ref="passwordStrength"
                        v-model="passwordObj"
                        :label="'Password'"
                      />
                    </v-col>
                    <v-col cols="12" class="pl-2">
                      <v-checkbox
                        color="primary"
                        required
                        :error-messages="errors.collect('terms')"
                        :rules="[() => !!terms || 'This field is required']"
                        invalidateFalse
                        v-validate="'required'"
                        v-model="terms"
                        name="terms"
                      >
                        <template v-slot:label>
                          By checking this box, you agree to our&nbsp;
                          <a target="_blank" href="/privacy-policy" @click.stop>
                            Privacy Policy</a
                          >&nbsp;and
                          <a
                            target="_blank"
                            href="/terms-and-conditions"
                            @click.stop
                          >
                            Terms &amp; Conditions
                          </a>
                          <br />
                        </template>
                      </v-checkbox>
                    </v-col>
                    <v-col cols="12">
                      <vue-recaptcha
                        ref="recaptcha"
                        @verify="onCaptchaVerified"
                        @expired="onCaptchaExpired"
                        sitekey="6Legt7EUAAAAAMn44zBe0LZGqWEaZFUKwQxrOqdW"
                        :loadRecaptchaScript="true"
                      ></vue-recaptcha>
                      <transition name="slide-y-transition">
                        <span
                          v-show="captchaRequired"
                          class="v-messages error--text"
                        >
                          {{ captchaRequired }}
                        </span>
                      </transition>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions class="px-3 pb-3 pt-0">
                  <div>
                    Already have an account?
                    <router-link to="/user/login" style="color: #1565c0">
                      Click here to log in.
                    </router-link>
                  </div>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    type="submit"
                    :loading="loading"
                    :disabled="loading"
                  >
                    Signup
                  </v-btn>
                </v-card-actions>
              </form>
            </v-card>
          </v-expand-transition>
        </v-col>
      </v-row>
      <custom-snackbar />
    </v-container>
  </section>
</template>

<script>
import { _constants } from 'crypto'
import { mapMutations, mapState } from 'vuex'
import CustomSnackbar from '@/components/snackbar/snackbar'
import HeaderLoggedOut from '@/modules/auth/views/HeaderLoggedOut'
import Individual from '@/modules/individual/models/Individual'
import VueRecaptcha from 'vue-recaptcha'
import PasswordStrength from '@/components/PasswordStrength'

export default {
  name: 'Signup',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    CustomSnackbar,
    VueRecaptcha,
    HeaderLoggedOut,
    PasswordStrength
  },
  computed: {
    ...mapState(['bucketUrl'])
  },
  data() {
    return {
      first_name: null,
      last_name: null,
      email: null,
      visible: false,
      password: null,
      password_confirmation: null,
      loading: false,
      terms: null,
      captchaVerified: false,
      captchaRequired: '',
      passwordObj: null
    }
  },
  methods: {
    ...mapMutations('snackbar', ['showSnackbar', 'showErrorSnackbar']),
    onCaptchaVerified() {
      this.captchaVerified = true
      this.captchaRequired = ''
    },
    onCaptchaExpired() {
      this.$refs.recaptcha.reset()
    },
    async submit() {
      // validate password strength
      let passwordResult = await this.$refs.passwordStrength.validate()
      if (!passwordResult) {
        return // there are errors
      }

      if (this.passwordObj.isWeak) {
        this.showErrorSnackbar('Your password is too weak')
        return // there are errors
      }
      // validate form
      let result = await this.$validator.validateAll()
      if (!result) return

      if (!this.captchaVerified) {
        this.captchaRequired = 'reCAPTCHA is required'
        return
      }

      if (!result) return // there are errors
      try {
        let form = {
          first_name: this.first_name.toLowerCase(),
          last_name: this.last_name.toLowerCase(),
          email: this.email.toLowerCase(),
          password: this.passwordObj.password,
          password_confirmation: this.passwordObj.confirmpassword
        }

        this.loading = true
        const individual = await Individual.dispatch('register', {
          payload: form
        })

        await Individual.dispatch('login', {
          payload: { password: form.password, username: form.email }
        })

        this.showSnackbar(
          `Welcome ${this.capitalizeFirstLetter(
            individual.first_name ? individual.first_name : individual.email
          )}. You will be redirected to your dashboard`
        )
        this.loading = false

        setTimeout(() => {
          this.$router.push('/user/dashboard')
        }, 2500)
      } catch (error) {
        this.loading = false
        if (error.status === 422) {
          this.showErrorSnackbar('One of the fields could not be validated')
          return
        }
        this.showErrorSnackbar('There was a server error. Contact support')
      }
    }
  }
}
</script>

<style lang="scss">
.individual-signup {
  height: 100%;
  width: 100%;
  background-size: cover;
  background-position: top;
  background-repeat: no-repeat;
}
</style>
